import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { fetchDebts, uploadMonthlyUpdate, setSelectedDebt } from '../../store/slices/debtsSlice';
import { downloadMonthlyTemplate } from '../../services/api/debtsApi';
import { useTranslation } from '../../hooks/useTranslation';
import { AgGridReact } from 'ag-grid-react';
import { ColDef, ModuleRegistry, AllCommunityModule } from 'ag-grid-community';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-quartz.css';
import PaymentDetailsModal from '../PaymentDetailsModal';
import FileUploadModal from '../FileUploadModal';
import NotificationModal from '../NotificationModal';
import './DebtsTab.css';

// Register AG Grid modules
ModuleRegistry.registerModules([AllCommunityModule]);

interface DebtsTabProps {
  onBack: () => void;
}

const DebtsTab: React.FC<DebtsTabProps> = ({ onBack }) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { debts, loading, error } = useAppSelector((state) => state.debts);
  
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [notification, setNotification] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    type: 'success' | 'error' | 'info' | 'warning';
  }>({
    isOpen: false,
    title: '',
    message: '',
    type: 'info'
  });

  useEffect(() => {
    dispatch(fetchDebts()).catch(() => {
      console.log('Using mock data');
    });
  }, [dispatch]);

  const handleViewPayments = useCallback((debt: typeof debts[0]) => {
    if (!debt || !debt.id) {
      console.error('Invalid debt object:', debt);
      setNotification({
        isOpen: true,
        title: t('common.error'),
        message: 'Invalid debt data. Please try again.',
        type: 'error'
      });
      return;
    }
    dispatch(setSelectedDebt(debt));
    setShowPaymentModal(true);
  }, [dispatch, t]);

  const handleDownloadTemplate = async () => {
    try {
      const blob = await downloadMonthlyTemplate();
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'monthly_template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading template:', error);
      setNotification({
        isOpen: true,
        title: t('debts.messages.downloadFailed'),
        message: t('debts.messages.downloadFailed'),
        type: 'error'
      });
    }
  };

  const handleUploadFile = async (file: File) => {
    try {
      setUploadLoading(true);
      await dispatch(uploadMonthlyUpdate(file)).unwrap();
      setNotification({
        isOpen: true,
        title: t('debts.messages.uploadSuccess'),
        message: t('debts.messages.uploadSuccess'),
        type: 'success'
      });
      setShowUploadModal(false);
      // Refresh debts list
      dispatch(fetchDebts());
    } catch (error: any) {
      console.error('Error uploading file:', error);
      setNotification({
        isOpen: true,
        title: t('debts.messages.uploadFailed'),
        message: error || t('debts.messages.uploadFailed'),
        type: 'error'
      });
    } finally {
      setUploadLoading(false);
    }
  };

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'MRU' // Adjust currency as needed
    }).format(amount);
  };

  const getShortId = (id: string): string => {
    // Get the last 8 characters of the UUID
    return id.split('-').pop() || id.substring(id.length - 8);
  };

  const calculateRemaining = (debt: typeof debts[0]): number => {
    return debt.totalDebt - (debt.totalPaid || 0);
  };

  const columnDefs: ColDef[] = useMemo(() => {
    // Action button component for AG Grid
    const ViewDetailsButtonCell = (params: any) => {
      const debt = params.data;
      
      if (!debt || !debt.id) {
        return <span>N/A</span>;
      }
      
      return (
        <button
          className="view-payments-button"
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            handleViewPayments(debt);
          }}
        >
          {t('debts.table.viewDetails')}
        </button>
      );
    };

    return [
    {
      field: 'id',
      headerName: t('debts.table.debtId'),
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      sortable: true,
      cellRenderer: (params: any) => {
        const shortId = getShortId(params.value);
        return React.createElement('code', null, shortId);
      },
      width: 150
    },
    {
      field: 'userNni',
      headerName: t('debts.table.userNni'),
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      sortable: true
    },
    {
      field: 'totalDebt',
      headerName: t('debts.table.totalDebt'),
      filter: 'agTextColumnFilter',
      filterParams: {
        textMatcher: ({ value, filterText }: any) => {
          if (!filterText) return true;
          const cellValue = value?.toString() || '0';
          return cellValue.includes(filterText);
        },
        debounceMs: 200
      },
      floatingFilter: true,
      sortable: true,
      cellRenderer: (params: any) => formatCurrency(params.value),
      valueFormatter: (params: any) => formatCurrency(params.value)
    },
    {
      field: 'totalPaid',
      headerName: t('debts.table.totalPaid'),
      filter: 'agTextColumnFilter',
      filterParams: {
        textMatcher: ({ value, filterText }: any) => {
          if (!filterText) return true;
          const cellValue = (value || 0)?.toString() || '0';
          return cellValue.includes(filterText);
        },
        debounceMs: 200
      },
      floatingFilter: true,
      sortable: true,
      cellRenderer: (params: any) => {
        const value = formatCurrency(params.value || 0);
        return React.createElement('span', { className: 'paid-amount' }, value);
      },
      valueFormatter: (params: any) => formatCurrency(params.value || 0)
    },
    {
      field: 'remaining',
      headerName: t('debts.table.totalRemaining'),
      filter: 'agTextColumnFilter',
      filterParams: {
        textMatcher: ({ value, filterText }: any) => {
          if (!filterText) return true;
          const cellValue = value?.toString() || '0';
          return cellValue.includes(filterText);
        },
        debounceMs: 200
      },
      floatingFilter: true,
      sortable: true,
      valueGetter: (params: any) => {
        return calculateRemaining(params.data);
      },
      cellRenderer: (params: any) => {
        const value = formatCurrency(params.value);
        return React.createElement('span', { className: 'remaining-amount' }, value);
      },
      valueFormatter: (params: any) => formatCurrency(params.value)
    },
    {
      field: 'numberOfInstallments',
      headerName: t('debts.table.installments'),
      filter: 'agTextColumnFilter',
      filterParams: {
        textMatcher: ({ value, filterText }: any) => {
          if (!filterText) return true;
          const cellValue = value?.toString() || '0';
          return cellValue.includes(filterText);
        },
        debounceMs: 200
      },
      floatingFilter: true,
      sortable: true,
      cellRenderer: (params: any) => params.value || '-',
      width: 130
    },
    {
      field: 'actions',
      headerName: t('common.actions'),
      sortable: false,
      filter: false,
      cellRenderer: ViewDetailsButtonCell,
      cellStyle: { display: 'flex', alignItems: 'center', justifyContent: 'center' },
      width: 180
    }
    ];
  }, [t, handleViewPayments, getShortId, formatCurrency, calculateRemaining]);

  const defaultColDef = useMemo(() => ({
    resizable: true,
    flex: 1,
    minWidth: 120
  }), []);

  const selectedDebt = useAppSelector((state) => state.debts.selectedDebt);

  const handleClosePaymentModal = useCallback(() => {
    setShowPaymentModal(false);
    dispatch(setSelectedDebt(null));
  }, [dispatch]);

  return (
    <div className="debts-tab">
      <div className="tab-header">
        <button className="back-button" onClick={onBack}>
          ← {t('common.back')}
        </button>
        <h1>{t('debts.title')}</h1>
      </div>

      {error && (
        <div className="error-message" style={{ color: 'red', marginBottom: '1rem' }}>
          {error}
        </div>
      )}

      <div className="action-buttons">
        <button 
          className="action-button download-button"
          onClick={handleDownloadTemplate}
        >
          {t('debts.downloadTemplate')}
        </button>
        <button 
          className="action-button upload-button"
          onClick={() => setShowUploadModal(true)}
        >
          {t('debts.uploadUpdate')}
        </button>
      </div>

      <div className="table-container ag-theme-quartz modern-ag-theme" style={{ height: '600px', width: '100%' }}>
        {loading ? (
          <div className="loading-message">{t('debts.loading')}</div>
        ) : debts.length === 0 ? (
          <div className="empty-message">{t('debts.empty')}</div>
        ) : (
          <AgGridReact
            rowData={debts}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            pagination={true}
            paginationPageSize={20}
            suppressCellFocus={true}
            domLayout="normal"
          />
        )}
      </div>

      {showPaymentModal && selectedDebt && (
        <PaymentDetailsModal
          key={selectedDebt.id}
          debt={selectedDebt}
          onClose={handleClosePaymentModal}
        />
      )}

      {showUploadModal && (
        <FileUploadModal
          onClose={() => setShowUploadModal(false)}
          onUpload={handleUploadFile}
          loading={uploadLoading}
          accept=".xlsx,.xls"
        />
      )}

      <NotificationModal
        isOpen={notification.isOpen}
        title={notification.title}
        message={notification.message}
        type={notification.type}
        onClose={() => setNotification({ ...notification, isOpen: false })}
      />
    </div>
  );
};

export default DebtsTab;

